import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useState } from "react";
import { editAppeal, editComplaint } from "services/advocateComplaintService";
import { AdvocateComplaints, Appeal } from "types";
import { LoadingIcon } from "utils/constant";
import {
  ADVOCATE_APPEAL_FEEDBACK_TYPE,
  APPEAL_STATUS,
  COMPLIANT_STATUS,
} from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface FeedbackModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: AdvocateComplaints;
  appealData: Appeal;
  onRefresh: any;
}

export default function AddAppealFeedbackModal({
  isOpen,
  setIsOpen,
  complaintData,
  appealData,
  onRefresh,
}: FeedbackModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);

  const [formState, setFormState] = useState<Appeal>({
    feedback_on_memorandum: "",
    appeal_status: ADVOCATE_APPEAL_FEEDBACK_TYPE.APPROVED,
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!appealData) return;

    const data = {
      appeal_type: appealData?.appeal_type,
      memorandum_summary: appealData?.memorandum_summary,
      requesting_prosecutor: appealData?.requesting_prosecutor,
      userid_requesting_prosecutor: appealData?.userid_requesting_prosecutor,
      ...formState,
      feedback_comment: formState.feedback_on_memorandum,
      appeal_feedback_status: formState.appeal_status,
      appeal_status:
        formState.appeal_status === ADVOCATE_APPEAL_FEEDBACK_TYPE.APPROVED
          ? APPEAL_STATUS.FORWARDED_TO_REGISTRAR
          : APPEAL_STATUS.REVISION_REQUIRED,
      discipline_case: appealData?.discipline_case || null,

      admin_unit_id: complaintData?.admin_unit_id || "",
      org_id: complaintData?.org_id || "",
      accused_advocate_name: complaintData?.accused_name || "",
      user_id_accused_advocate: complaintData?.userid_accused_name || "",
      accused_license_number: complaintData?.accused_license_number || "",
    };
    const appeal_id = appealData?.id || "";
    const isFormData = false;

    setLoading(true);
    try {
      const resData = await editAppeal(data, appeal_id, isFormData);

      if (
        resData &&
        formState.appeal_status === ADVOCATE_APPEAL_FEEDBACK_TYPE.APPROVED
      ) {
        await editComplaint({
          ...complaintData,
          has_appeal: true,
          status: COMPLIANT_STATUS.APPEALED,
        });
      }

      onRefresh();
      setIsOpen(false);
      showToast(t("successfullyCreated"), "success");

      setFormState({
        feedback_on_memorandum: "",
        appeal_status: APPEAL_STATUS.APPROVED,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addMemorandumFeedback")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("feedbackType")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="appeal_status"
              value={formState.appeal_status ?? ""}
              onChange={handleChange}
            >
              {Object.values(ADVOCATE_APPEAL_FEEDBACK_TYPE)?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("feedbackOnMemorandum")}
            </label>
            <textarea
              required
              rows={7}
              name="feedback_on_memorandum"
              value={formState.feedback_on_memorandum}
              onChange={handleChange}
              placeholder={t("detailsHere")}
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
            />
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("addFeedback")
          )}
        </button>
      </form>
    </SideModal>
  );
}
